import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, User } from 'lucide-react'
import toast from 'react-hot-toast'
import { showError, showSuccess } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { inboxTasksService } from '@/services/inboxTasksService'
import { approvalRouteDetailService, type ApprovalUserItem } from '@/services/approvalRouteDetailService'
import { employeesService } from '@/services/employeesService'
import { loanRequestService } from '@/services/loanRequestService'
import { leaveRequestService } from '@/services/leaveRequestService'
import { expenseClaimService } from '@/services/expenseClaimService'
import { hireEmployeeRequestService } from '@/services/hireEmployeeRequestService'
import { purchaseRequisitionService } from '@/services/purchaseRequisitionService'
import { itemRequestApiService } from '@/services/itemRequestApiService.ts'
import { overTimeRequestApiService } from '@/services/overTimeRequestApiService'
import { config } from '@/core/config'
import type {
  InboxTaskListItem,
  ApprovalStatusItem,
  LoanLookupItem,
  LoanRequestVm,
  LeaveRequestVm,
  LeaveRequestLookupItem,
  ExpenseClaimVm,
  ItemRequestVm,
  OverTimeRequestVm,
} from '@/api/types'
import type { Employee } from '@/api/types'
import type { DepartmentLookup } from '@/api/types'

const EMPLOYEE_APPROVAL_RECORD_NAME = 'Employee Approval Request'
const LOAN_APPROVAL_RECORD_NAME = 'Loan Request'
const LEAVE_APPROVAL_RECORD_NAME = 'Leave Request'
const EXPENSE_CLAIM_APPROVAL_RECORD_NAME = 'Expense Claim Request'
const HIRE_EMPLOYEE_APPROVAL_RECORD_NAME = 'Hire Employee Request'
const PURCHASE_REQUISITION_APPROVAL_RECORD_NAME = 'Purchase Requisition Request'
const ITEM_REQUEST_APPROVAL_RECORD_NAME = 'Item Request'
const OVERTIME_APPROVAL_RECORD_NAME = 'Over Time Request'
const normalizeRecordName = (s?: string | null) => (s ?? '').toLowerCase().replace(/\s+/g, '').trim()
const isEmployeeApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(EMPLOYEE_APPROVAL_RECORD_NAME)
const isLoanApprovalRecordName = (s?: string | null) => normalizeRecordName(s) === normalizeRecordName(LOAN_APPROVAL_RECORD_NAME)
const isLeaveApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(LEAVE_APPROVAL_RECORD_NAME)
const isExpenseClaimApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(EXPENSE_CLAIM_APPROVAL_RECORD_NAME)
const isHireEmployeeApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(HIRE_EMPLOYEE_APPROVAL_RECORD_NAME)
const isPurchaseRequisitionApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(PURCHASE_REQUISITION_APPROVAL_RECORD_NAME)
const isItemRequestApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(ITEM_REQUEST_APPROVAL_RECORD_NAME)
const isOvertimeApprovalRecordName = (s?: string | null) =>
  normalizeRecordName(s) === normalizeRecordName(OVERTIME_APPROVAL_RECORD_NAME)

type HireEmployeeRequestVm = {
  id: number
  empId: number
  departmentId: number
  jobId: number
  position: string
  salaryRangeFrom: number
  salaryRangeTo: number
  trainingCourses: string
  yearsOfExperience: number
  details: string
  otherRequiredQualification: string
  educationalQualification: string
  attachmentUrl: string
  status: number
  approvalDate: string | null
  createdDate: string
  createdBy: number
  branchId: number
  isEmployeeCreated: boolean
  empFirstNameEn: string
  empFatherNameEn: string
  empGrandNameEn: string
  empFamilyNameEn: string
  empFirstNameAr: string
  empFatherNameAr: string
  empGrandNameAr: string
  empFamilyNameAr: string
}

function mapHireEmployeeRequestVm(raw: unknown): HireEmployeeRequestVm | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const pick = (c: string, p: string) => String(r[c] ?? r[p] ?? '').trim()
  return {
    id: Number(r.id ?? r.Id ?? 0),
    empId: Number(r.empId ?? r.EmpId ?? 0),
    departmentId: Number(r.departmentId ?? r.DepartmentId ?? 0),
    jobId: Number(r.jobId ?? r.JobId ?? 0),
    position: String(r.position ?? r.Position ?? ''),
    salaryRangeFrom: Number(r.salaryRangeFrom ?? r.SalaryRangeFrom ?? 0),
    salaryRangeTo: Number(r.salaryRangeTo ?? r.SalaryRangeTo ?? 0),
    trainingCourses: String(r.trainingCourses ?? r.TrainingCourses ?? ''),
    yearsOfExperience: Number(r.yearsOfExperience ?? r.YearsOfExperience ?? 0),
    details: String(r.details ?? r.Details ?? ''),
    otherRequiredQualification: String(r.otherRequiredQualification ?? r.OtherRequiredQualification ?? ''),
    educationalQualification: String(r.educationalQualification ?? r.EducationalQualification ?? ''),
    attachmentUrl: String(r.attachmentUrl ?? r.AttachmentUrl ?? ''),
    status: Number(r.status ?? r.Status ?? 0),
    approvalDate: (() => {
      const v = r.approvalDate ?? r.ApprovalDate
      return v != null && String(v).trim() !== '' ? String(v) : null
    })(),
    createdDate: String(r.createdDate ?? r.CreatedDate ?? ''),
    createdBy: Number(r.createdBy ?? r.CreatedBy ?? 0),
    branchId: Number(r.branchId ?? r.BranchId ?? 0),
    isEmployeeCreated: Boolean(r.isEmployeeCreated ?? r.IsEmployeeCreated ?? false),
    empFirstNameEn: pick('empFirstNameEn', 'EmpFirstNameEn'),
    empFatherNameEn: pick('empFatherNameEn', 'EmpFatherNameEn'),
    empGrandNameEn: pick('empGrandNameEn', 'EmpGrandNameEn'),
    empFamilyNameEn: pick('empFamilyNameEn', 'EmpFamilyNameEn'),
    empFirstNameAr: pick('empFirstNameAr', 'EmpFirstNameAr'),
    empFatherNameAr: pick('empFatherNameAr', 'EmpFatherNameAr'),
    empGrandNameAr: pick('empGrandNameAr', 'EmpGrandNameAr'),
    empFamilyNameAr: pick('empFamilyNameAr', 'EmpFamilyNameAr'),
  }
}

function approverActionBadgeVariant(action: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const a = (action || '').toLowerCase()
  if (a.includes('reject')) return 'destructive'
  if (a.includes('approv') && !a.includes('pending')) return 'default'
  if (a.includes('pending')) return 'secondary'
  return 'outline'
}

function hireCandidateFullName(vm: HireEmployeeRequestVm): string {
  const en = [vm.empFirstNameEn, vm.empFatherNameEn, vm.empGrandNameEn, vm.empFamilyNameEn]
    .filter((s) => s && String(s).trim())
    .join(' ')
    .trim()
  if (en) return en
  const ar = [vm.empFirstNameAr, vm.empFatherNameAr, vm.empGrandNameAr, vm.empFamilyNameAr]
    .filter((s) => s && String(s).trim())
    .join(' ')
    .trim()
  return ar || ''
}

function absoluteUploadUrl(path: string): string {
  const p = String(path || '').trim()
  if (!p) return ''
  if (/^https?:\/\//i.test(p)) return p
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  const rel = p.replace(/^\/+/, '')
  return base ? `${base}/${rel}` : p
}

/**
 * Approval Detail – opens when Task Inbox action button is clicked.
 * Loads record by id (GetById), then:
 * - Top left: Employee/Request detail (if recordName is Employee Approval Request, load employee by recordId)
 * - Bottom left: Approvers (GetApprovalUsers by approvalTaskId)
 * - Right: Requested By (GetUserById requestedBy → empId → employee name), Record Name, Department, Approval Status, Approval Notes, Submit (ApproveRequest)
 */
export const ApprovalDetail: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [data, setData] = useState<InboxTaskListItem | null>(null)
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [loanRequest, setLoanRequest] = useState<LoanRequestVm | null>(null)
  const [leaveRequest, setLeaveRequest] = useState<LeaveRequestVm | null>(null)
  const [expenseClaim, setExpenseClaim] = useState<ExpenseClaimVm | null>(null)
  const [hireEmployeeRequest, setHireEmployeeRequest] = useState<HireEmployeeRequestVm | null>(null)
  const [purchaseRequisition, setPurchaseRequisition] = useState<any | null>(null)
  const [purchaseRequisitionLines, setPurchaseRequisitionLines] = useState<any[]>([])
  const [purchaseRequisitionItemsById, setPurchaseRequisitionItemsById] = useState<Record<number, string>>({})
  const [itemRequest, setItemRequest] = useState<ItemRequestVm | null>(null)
  const [itemRequestItemsById, setItemRequestItemsById] = useState<Record<number, string>>({})
  const [overtimeRequest, setOvertimeRequest] = useState<OverTimeRequestVm | null>(null)
  const [approvers, setApprovers] = useState<ApprovalUserItem[]>([])
  const [approverEmpNames, setApproverEmpNames] = useState<Record<number, string>>({})
  const [requestedByName, setRequestedByName] = useState<string>('')
  const [departmentName, setDepartmentName] = useState<string>('')
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [approvalStatusId, setApprovalStatusId] = useState<number>(0)
  const [approvalNote, setApprovalNote] = useState<string>('')
  const [loanTypes, setLoanTypes] = useState<LoanLookupItem[]>([])
  const [leaveTypes, setLeaveTypes] = useState<LeaveRequestLookupItem[]>([])
  const [leaveSubstituteName, setLeaveSubstituteName] = useState<string>('—')

  const recordId = data?.recordId ?? 0
  const approvalTaskId = data?.approvalTaskId ?? 0
  const requestedBy = data?.requestedBy ?? 0

  // Load main record by id
  useEffect(() => {
    if (!id || !branchId) return
    const numId = parseInt(id, 10)
    if (!numId) return

    const load = async () => {
      setLoading(true)
      try {
        const record = await inboxTasksService.getById(numId)
        setData(record)
        if (record) {
          setApprovalStatusId(record.approvalStatusId ?? 0)
          setApprovalNote(record.approvalNote ?? '')
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || t('approvalDetailPage.toastLoadFailed'))
        setData(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, branchId])

  // Load departments and statuses for dropdowns
  useEffect(() => {
    const load = async () => {
      try {
        const [depts, st] = await Promise.all([
          employeesService.getDepartmentLookups(),
          inboxTasksService.getApprovalStatuses(),
        ])
        setDepartments(depts ?? [])
        setStatuses(st ?? [])
      } catch {
        setDepartments([])
        setStatuses([])
      }
    }
    load()
  }, [])

  // When recordName is Employee Approval Request, load employee by recordId (top-left section)
  useEffect(() => {
    if (!data || !isEmployeeApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setEmployee(null)
      return
    }
    const load = async () => {
      try {
        const emp = await employeesService.getEmployeeById(branchId, recordId)
        setEmployee(emp)
      } catch {
        setEmployee(null)
      }
    }
    load()
  }, [data, recordId, branchId])

  // When recordName is Loan Request, load loan by recordId and then load related employee via loan.empId
  useEffect(() => {
    if (!data || !isLoanApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setLoanRequest(null)
      setEmployee(null)
      return
    }

    const load = async () => {
      try {
        const loan = await loanRequestService.getById(recordId, branchId)
        setLoanRequest(loan)

        const empId = loan?.empId ?? 0
        if (!empId) {
          setEmployee(null)
          return
        }
        const emp = await employeesService.getEmployeeById(branchId, empId)
        setEmployee(emp)
      } catch {
        setLoanRequest(null)
        setEmployee(null)
      }
    }

    load()
  }, [data, recordId, branchId])

  // Load loan types only for Loan Request approval pages
  useEffect(() => {
    if (!data || !isLoanApprovalRecordName(data.recordName)) {
      setLoanTypes([])
      return
    }

    const load = async () => {
      try {
        const list = await loanRequestService.getLoanTypes()
        setLoanTypes(list || [])
      } catch {
        setLoanTypes([])
      }
    }

    load()
  }, [data])

  // When recordName is Leave Request, load leave by recordId and then load related employee via leave.empId
  useEffect(() => {
    if (!data || !isLeaveApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setLeaveRequest(null)
      setEmployee(null)
      return
    }

    const load = async () => {
      try {
        const leave = await leaveRequestService.getById(recordId, branchId)
        setLeaveRequest(leave)

        const empId = leave?.empId ?? 0
        if (!empId) {
          setEmployee(null)
          return
        }

        const emp = await employeesService.getEmployeeById(branchId, empId)
        setEmployee(emp)
      } catch {
        setLeaveRequest(null)
        setEmployee(null)
      }
    }

    load()
  }, [data, recordId, branchId])

  // When recordName is Expense Claim Request, load expense claim by recordId, then load related employee via claim.empId/employeeId
  useEffect(() => {
    if (!data || !isExpenseClaimApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setExpenseClaim(null)
      return
    }

    const load = async () => {
      try {
        const claim = await expenseClaimService.getById(recordId, branchId)
        setExpenseClaim(claim)

        const empId = claim?.employeeId ?? 0
        if (!empId) {
          setEmployee(null)
          return
        }
        const emp = await employeesService.getEmployeeById(branchId, empId)
        setEmployee(emp)
      } catch {
        setExpenseClaim(null)
        setEmployee(null)
      }
    }

    load()
  }, [data, recordId, branchId])

  // When recordName is Hire Employee Request, load hire request by recordId (needs branchId too), then load employee via empId
  useEffect(() => {
    if (!data || !isHireEmployeeApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setHireEmployeeRequest(null)
      return
    }
    const load = async () => {
      try {
        const raw = await hireEmployeeRequestService.getById(recordId, branchId)
        const inner = raw && typeof raw === 'object' && 'data' in raw ? (raw as { data: unknown }).data : raw
        const vm = mapHireEmployeeRequestVm(inner)
        setHireEmployeeRequest(vm)

        const empId = vm?.empId ?? 0
        if (empId > 0) {
          const emp = await employeesService.getEmployeeById(branchId, empId)
          setEmployee(emp)
        }
      } catch {
        setHireEmployeeRequest(null)
      }
    }
    load()
  }, [data, recordId, branchId])

  // When recordName is Purchase Requisition Request, load requisition master + lines; resolve item names; set department name from DepartmentId.
  useEffect(() => {
    if (!data || !isPurchaseRequisitionApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setPurchaseRequisition(null)
      setPurchaseRequisitionLines([])
      setPurchaseRequisitionItemsById({})
      return
    }
    const load = async () => {
      try {
        const [m, lines, items] = await Promise.all([
          purchaseRequisitionService.getById(recordId, branchId),
          purchaseRequisitionService.getLinesByRequisitionId(recordId, branchId),
          purchaseRequisitionService.getItems(branchId),
        ])
        setPurchaseRequisition(m)
        setPurchaseRequisitionLines(lines || [])
        const map: Record<number, string> = {}
        ;(items || []).forEach((x: { id: number; name: string }) => {
          map[x.id] = x.name
        })
        setPurchaseRequisitionItemsById(map)

        const deptId = Number(m?.departmentId ?? 0)
        const found = departments.find((d) => d.id === deptId)
        const depName = found ? (found.lookupNameEn || found.lookupNameAr || '') : ''
        setDepartmentName(depName || '')
      } catch {
        setPurchaseRequisition(null)
        setPurchaseRequisitionLines([])
        setPurchaseRequisitionItemsById({})
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, recordId, branchId])

  // When recordName is Item Request, load item request by recordId; resolve item & department labels.
  useEffect(() => {
    if (!data || !isItemRequestApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setItemRequest(null)
      setItemRequestItemsById({})
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const [vm, items] = await Promise.all([
          itemRequestApiService.getById(recordId, branchId),
          purchaseRequisitionService.getItems(branchId),
        ])
        if (cancelled) return
        setItemRequest(vm)
        const map: Record<number, string> = {}
        ;(items || []).forEach((x: { id: number; name: string }) => {
          map[x.id] = x.name
        })
        setItemRequestItemsById(map)

        const deptId = Number(vm?.departmentId ?? 0)
        const found = departments.find((d) => d.id === deptId)
        const depName = found ? (found.lookupNameEn || found.lookupNameAr || '') : ''
        setDepartmentName(depName || '')
      } catch {
        if (!cancelled) {
          setItemRequest(null)
          setItemRequestItemsById({})
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, recordId, branchId, departments])

  // When recordName is Over Time Request, load overtime request by recordId (Self Service API)
  useEffect(() => {
    if (!data || !isOvertimeApprovalRecordName(data.recordName) || !recordId || !branchId) {
      setOvertimeRequest(null)
      return
    }
    let cancelled = false
    void overTimeRequestApiService
      .getById(recordId, branchId)
      .then((vm) => {
        if (!cancelled) setOvertimeRequest(vm)
      })
      .catch(() => {
        if (!cancelled) setOvertimeRequest(null)
      })
    return () => {
      cancelled = true
    }
  }, [data, recordId, branchId])

  const approvalCodeFromId = (id: number) =>
    statusOptions.find((s) => s.id === id)?.lookupCodeId ?? id

  const validateItemRequestAvailability = async (): Promise<boolean> => {
    if (!data || !isItemRequestApprovalRecordName(data.recordName)) return true
    if (!itemRequest || !branchId) return true

    const itemId = Number(itemRequest.itemId || 0)
    const departmentId = Number(itemRequest.departmentId || 0)
    const requestedQty = Number(itemRequest.quantity || 0)
    if (!itemId || !departmentId || !requestedQty) return true

    const available = await itemRequestApiService.getAvailableQuantityForEmployeeDepartment({
      itemId,
      branchId,
      departmentId,
    })

    if (available < requestedQty) {
      await showError(
        t('approvalDetailPage.qtyInsufficientTitle', { defaultValue: 'Insufficient Quantity' }),
        t('approvalDetailPage.qtyInsufficientMessage', {
          defaultValue: 'Requested quantity exceeds available quantity.',
          available,
          requested: requestedQty,
        }),
      )
      return false
    }

    return true
  }

  // Load leave types only for Leave Request approval pages
  useEffect(() => {
    if (!data || !isLeaveApprovalRecordName(data.recordName)) {
      setLeaveTypes([])
      return
    }

    const load = async () => {
      try {
        const list = await leaveRequestService.getLeaveTypes()
        setLeaveTypes(list || [])
      } catch {
        setLeaveTypes([])
      }
    }

    load()
  }, [data])

  // Load substitute employee name (optional)
  useEffect(() => {
    const subId = leaveRequest?.substituteEmpId ?? 0
    if (!data || !isLeaveApprovalRecordName(data.recordName) || !branchId) return

    if (!subId) {
      setLeaveSubstituteName('—')
      return
    }

    const load = async () => {
      try {
        const emp = await employeesService.getEmployeeById(branchId, subId)
        const name = [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ') || emp.empFirstNameAr || ''
        setLeaveSubstituteName(name || '—')
      } catch {
        setLeaveSubstituteName('—')
      }
    }

    load()
  }, [data, branchId, leaveRequest])

  // Approvers list: GetApprovalUsers(branchId, approvalTaskId)
  useEffect(() => {
    if (!branchId || !approvalTaskId) {
      setApprovers([])
      return
    }
    const load = async () => {
      try {
        const list = await approvalRouteDetailService.getApprovalUsers(branchId, approvalTaskId)
        setApprovers(list)
      } catch {
        setApprovers([])
      }
    }
    load()
  }, [branchId, approvalTaskId])

  // Resolve approver display names from employee master (cleaner than raw userName from API)
  useEffect(() => {
    if (!branchId || approvers.length === 0) {
      setApproverEmpNames({})
      return
    }
    const ids = [
      ...new Set(
        approvers.map((a) => a.empId).filter((id): id is number => typeof id === 'number' && id > 0)
      ),
    ]
    if (ids.length === 0) {
      setApproverEmpNames({})
      return
    }
    let cancelled = false
    void (async () => {
      const next: Record<number, string> = {}
      await Promise.all(
        ids.map(async (empId) => {
          try {
            const emp = await employeesService.getEmployeeById(branchId, empId)
            const name = [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim()
            next[empId] = name || emp.empFirstNameAr || emp.empCode || ''
          } catch {
            next[empId] = ''
          }
        })
      )
      if (!cancelled) setApproverEmpNames(next)
    })()
    return () => {
      cancelled = true
    }
  }, [branchId, approvers])

  // Requested By: GetUserById(branchId, requestedBy) → empId → get employee name
  useEffect(() => {
    if (!branchId || !requestedBy) {
      setRequestedByName('')
      return
    }
    const load = async () => {
      try {
        const u = await approvalRouteDetailService.getUserById(branchId, requestedBy)
        if (!u?.empId) {
          setRequestedByName(u?.userName ?? '')
          return
        }
        const emp = await employeesService.getEmployeeById(branchId, u.empId)
        const name = [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ') || emp.empFirstNameAr || ''
        setRequestedByName(name || u.userName || '')
      } catch {
        setRequestedByName('')
      }
    }
    load()
  }, [branchId, requestedBy])

  // Department name: from employee (recordId) using departments lookup
  useEffect(() => {
    if (data && isPurchaseRequisitionApprovalRecordName(data.recordName)) return
    if (data && isItemRequestApprovalRecordName(data.recordName)) return
    if (!employee) {
      setDepartmentName('')
      return
    }
    const deptId = employee.empDepartmetId ?? 0
    if (!deptId) {
      setDepartmentName(employee.departmentName ?? '')
      return
    }
    const found = departments.find((d) => d.id === deptId)
    setDepartmentName(found ? (found.lookupNameEn || found.lookupNameAr || '') : (employee.departmentName ?? ''))
  }, [employee, departments])

  const statusOptions = useMemo(() => statuses.filter((s) => s.id), [statuses])

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateStr
    }
  }

  const formatApproverActionDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return dateStr
      return d.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateStr
    }
  }

  const formatTimeHm = (iso?: string | null) => {
    if (!iso) return '—'
    const s = String(iso)
    const hm = s.length >= 16 ? s.slice(11, 16) : ''
    return hm && hm.includes(':') ? hm : s
  }

  const translateRecordName = (s?: string | null) => {
    if (!s) return ''
    const n = normalizeRecordName(s)
    if (n === normalizeRecordName(EMPLOYEE_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNameEmployee')
    if (n === normalizeRecordName(LOAN_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNameLoan')
    if (n === normalizeRecordName(LEAVE_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNameLeave')
    if (n === normalizeRecordName(EXPENSE_CLAIM_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNameExpense')
    if (n === normalizeRecordName(HIRE_EMPLOYEE_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNameHireEmployee', { defaultValue: 'Hire Employee Request' })
    if (n === normalizeRecordName(PURCHASE_REQUISITION_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNamePurchaseRequisition', { defaultValue: 'Purchase Requisition Request' })
    if (n === normalizeRecordName(ITEM_REQUEST_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNameItemRequest', { defaultValue: 'Item Request' })
    if (n === normalizeRecordName(OVERTIME_APPROVAL_RECORD_NAME)) return t('approvalDetailPage.recordNameOvertime', { defaultValue: 'Over Time Request' })
    return s
  }

  const req = t('approvalDetailPage.requiredMark')

  const formatMoney = (n?: number | null) => {
    const v = Number(n ?? 0)
    if (!Number.isFinite(v)) return '—'
    return v.toFixed(4)
  }

  const handleSubmit = async () => {
    if (!data?.id) return
    const note = approvalNote.trim()
    if (!approvalStatusId) {
      toast.error(t('approvalDetailPage.toastSelectApprovalStatus'))
      return
    }
    setSubmitting(true)
    const payload = { id: data.id, approvalNote: note, approvalStatusId }
    console.log('Approval Detail – Update payload:', JSON.stringify(payload, null, 2))
    try {
      await approvalRouteDetailService.approveRequest(data.id, note, approvalStatusId)
      await showSuccess(t('approvalDetailPage.submittedTitle'), t('approvalDetailPage.submittedMessage'))
      navigate('/approval-jobs')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('approvalDetailPage.toastSubmitFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const loanTypeLabel = (lt: LoanLookupItem) =>
    lt.lookupNameEn || lt.lookupNameAr || lt.nameEn || lt.nameAr || lt.name || String(lt.codeType ?? lt.id)

  const employeeFullName = (emp: Employee | null) => {
    if (!emp) return ''
    return [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ') || emp.empFirstNameAr || ''
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('navigation.workflow')} description={t('approvalDetailPage.pageDescription')} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: two sections */}
        <div className="lg:col-span-2 space-y-6">
          {/* Top left: Employee / Request detail */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">
                {isLoanApprovalRecordName(data.recordName)
                  ? t('approvalDetailPage.sectionLoanRequest')
                  : isLeaveApprovalRecordName(data.recordName)
                    ? t('approvalDetailPage.sectionLeaveRequest')
                    : isExpenseClaimApprovalRecordName(data.recordName)
                      ? t('approvalDetailPage.sectionExpenseClaim')
                      : isHireEmployeeApprovalRecordName(data.recordName)
                        ? t('approvalDetailPage.sectionHireEmployeeRequest', { defaultValue: 'HIRE EMPLOYEE REQUEST' })
                      : isPurchaseRequisitionApprovalRecordName(data.recordName)
                        ? t('approvalDetailPage.sectionPurchaseRequisition', { defaultValue: 'PURCHASE REQUISITION DETAIL' })
                      : isItemRequestApprovalRecordName(data.recordName)
                        ? t('approvalDetailPage.sectionItemRequest', { defaultValue: 'ITEM REQUEST DETAIL' })
                      : isOvertimeApprovalRecordName(data.recordName)
                        ? t('approvalDetailPage.sectionOvertimeRequest', { defaultValue: 'OVER TIME REQUEST DETAIL' })
                    : t('approvalDetailPage.sectionEmployeeRequest')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {isPurchaseRequisitionApprovalRecordName(data.recordName) && purchaseRequisition ? (
                <div className="overflow-x-auto rounded-md border border-border/80">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b-2 border-primary/15 bg-muted/40 hover:bg-muted/40">
                        <TableHead className="font-semibold text-primary">
                          {t('approvalDetailPage.prColItem', { defaultValue: 'Item' })}
                        </TableHead>
                        <TableHead className="w-[160px] text-right font-semibold text-primary">
                          {t('approvalDetailPage.prColQty', { defaultValue: 'Quantity' })}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(purchaseRequisitionLines || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} className="py-6 text-center text-sm text-muted-foreground">
                            {t('common.noData')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        (purchaseRequisitionLines || []).map((ln: any) => {
                          const itemId = Number(ln?.itemId ?? ln?.ItemId ?? 0)
                          const qty = Number(ln?.quantity ?? ln?.Quantity ?? 0)
                          const name = purchaseRequisitionItemsById[itemId] || String(itemId || '—')
                          return (
                            <TableRow key={String(ln?.id ?? ln?.Id ?? `${itemId}-${qty}`)} className="border-b border-border/60">
                              <TableCell className="text-sm">{name}</TableCell>
                              <TableCell className="text-right text-sm">{Number.isFinite(qty) ? qty : '—'}</TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : isItemRequestApprovalRecordName(data.recordName) && itemRequest ? (
                <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  <div className="text-muted-foreground">{t('itemRequestPage.colItem', { defaultValue: 'Item' })}</div>
                  <div>
                    {(() => {
                      const itemId = Number(itemRequest.itemId ?? 0)
                      return itemRequestItemsById[itemId] || String(itemId || '—')
                    })()}
                  </div>

                  <div className="text-muted-foreground">{t('itemRequestPage.colQuantity', { defaultValue: 'Quantity' })}</div>
                  <div>{Number(itemRequest.quantity ?? 0) || '—'}</div>

                  <div className="text-muted-foreground">{t('itemRequestPage.colDepartment', { defaultValue: 'Department' })}</div>
                  <div>{departmentName || (itemRequest.departmentId ? String(itemRequest.departmentId) : '—')}</div>

                  <div className="text-muted-foreground">{t('itemRequestPage.colRequiredDate', { defaultValue: 'Request Date' })}</div>
                  <div>{formatDate(itemRequest.requestedDate || null)}</div>

                  <div className="text-muted-foreground">{t('itemRequestPage.labelDetails', { defaultValue: 'Details' })}</div>
                  <div className="break-words">{itemRequest.details ?? '—'}</div>
                </div>
              ) : isEmployeeApprovalRecordName(data.recordName) && employee ? (
                <div className="flex gap-4 flex-wrap">
                  <Avatar className="h-20 w-20 rounded-full">
                    <AvatarFallback>
                      <User className="h-10 w-10" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empFirstName')}:</span> {employee.empFirstNameEn ?? '—'}</div>
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empSecondName')}:</span> {employee.empSecondNameEn ?? '—'}</div>
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empFamilyName')}:</span> {employee.empLastNameEn ?? '—'}</div>
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empEmail')}:</span> {employee.empEmail ?? '—'}</div>
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empDob')}:</span> {formatDate(employee.empDob)}</div>
                    <div className="col-span-2"><span className="text-muted-foreground">{t('approvalDetailPage.empDepartment')}:</span> {employee.departmentName ?? (departmentName || '—')}</div>
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empNationalityId')}:</span> {employee.empNationalityId ?? '—'}</div>
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empAddress')}:</span> {employee.empAddress ?? '—'}</div>
                    <div><span className="text-muted-foreground">{t('approvalDetailPage.empReference')}:</span> {employee.empRefrence ?? employee.empReferenceContact ?? '—'}</div>
                  </div>
                </div>
              ) : isLoanApprovalRecordName(data.recordName) && loanRequest && employee ? (
                <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  <div className="text-muted-foreground">{t('approvalDetailPage.lblEmployeeName')}</div>
                  <div>{employeeFullName(employee) || '—'}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblLoanType')}</div>
                  <div>
                    {(() => {
                      const selected = loanTypes.find((x) => x.id === (loanRequest.loanTypeId ?? 0))
                      return selected ? loanTypeLabel(selected) : '—'
                    })()}
                  </div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblStartDate')}</div>
                  <div>{formatDate(loanRequest.startDate)}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblLoanAmount')}</div>
                  <div>{loanRequest.loanAmount ?? '—'}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblDeductionAmount')}</div>
                  <div>{loanRequest.deductAmount ?? '—'}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblDetails')}</div>
                  <div className="break-words">{loanRequest.details ?? '—'}</div>
                </div>
              ) : isLeaveApprovalRecordName(data.recordName) && leaveRequest && employee ? (
                <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  <div className="text-muted-foreground">{t('approvalDetailPage.lblEmployeeName')}</div>
                  <div>{employeeFullName(employee) || '—'}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblLeaveType')}</div>
                  <div>
                    {(() => {
                      const selected = leaveTypes.find((x) => x.id === (leaveRequest.leaveTypeId ?? 0))
                      if (!selected) return '—'
                      return selected.name ?? selected.nameEn ?? selected.lookupNameEn ?? '—'
                    })()}
                  </div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblFrom')}</div>
                  <div>{formatDate(leaveRequest.fromDate || null)}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblTo')}</div>
                  <div>{formatDate(leaveRequest.toDate || null)}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblSubstituteId')}</div>
                  <div>{leaveRequest.substituteEmpId ? leaveSubstituteName : '—'}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblReason')}</div>
                  <div className="break-words">{leaveRequest.details ?? '—'}</div>
                </div>
              ) : isExpenseClaimApprovalRecordName(data.recordName) && expenseClaim ? (
                <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  <div className="text-muted-foreground">{t('approvalDetailPage.lblEmployeeName')}</div>
                  <div>{employeeFullName(employee) || '—'}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblClaimDate')}</div>
                  <div>{formatDate(expenseClaim.claimDate || expenseClaim.expenseDate || null)}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblDetails')}</div>
                  <div className="break-words">{expenseClaim.details || '—'}</div>

                  <div className="text-muted-foreground">{t('approvalDetailPage.lblAmount')}</div>
                  <div>{formatMoney(expenseClaim.amount ?? 0)}</div>
                </div>
              ) : isHireEmployeeApprovalRecordName(data.recordName) && hireEmployeeRequest ? (
                <div className="grid gap-6 lg:grid-cols-4">
                  <div className="flex flex-col items-start gap-3 lg:col-span-1">
                    <Label>{t('approvalDetailPage.hireEmployeeProfileImage', { defaultValue: 'Profile Image' })}</Label>
                    <Avatar className="h-36 w-36 border border-border/80 bg-muted">
                      {hireEmployeeRequest.attachmentUrl ? (
                        <AvatarImage
                          src={absoluteUploadUrl(hireEmployeeRequest.attachmentUrl)}
                          alt=""
                          className="h-full w-full object-contain object-center"
                        />
                      ) : null}
                      <AvatarFallback className="text-lg text-muted-foreground">
                        {(() => {
                          const n = hireCandidateFullName(hireEmployeeRequest)
                          const parts = n.split(/\s+/).filter(Boolean)
                          const ini = parts
                            .slice(0, 2)
                            .map((p) => p[0])
                            .join('')
                          return ini ? ini.toUpperCase() : '?'
                        })()}
                      </AvatarFallback>
                    </Avatar>
                    {!hireEmployeeRequest.attachmentUrl ? (
                      <span className="text-xs text-muted-foreground">
                        {t('approvalDetailPage.hireEmployeeNoImage', { defaultValue: 'No image' })}
                      </span>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm lg:col-span-3">
                    <div className="text-muted-foreground">
                      {t('approvalDetailPage.hireEmployeeCandidateName', { defaultValue: 'Candidate full name' })}
                    </div>
                    <div className="font-medium">{hireCandidateFullName(hireEmployeeRequest) || '—'}</div>

                    <div className="text-muted-foreground">{t('approvalDetailPage.hireEmployeePosition', { defaultValue: 'Position' })}</div>
                    <div>{hireEmployeeRequest.position || '—'}</div>

                    <div className="text-muted-foreground">{t('approvalDetailPage.hireEmployeeSalaryFrom', { defaultValue: 'Salary Range From' })}</div>
                    <div>{formatMoney(hireEmployeeRequest.salaryRangeFrom)}</div>

                    <div className="text-muted-foreground">{t('approvalDetailPage.hireEmployeeSalaryTo', { defaultValue: 'Salary Range To' })}</div>
                    <div>{formatMoney(hireEmployeeRequest.salaryRangeTo)}</div>

                    <div className="text-muted-foreground">{t('approvalDetailPage.hireEmployeeYears', { defaultValue: 'Years Of Experience' })}</div>
                    <div>{hireEmployeeRequest.yearsOfExperience ?? '—'}</div>

                    <div className="text-muted-foreground">{t('approvalDetailPage.lblDetails')}</div>
                    <div className="break-words">{hireEmployeeRequest.details || '—'}</div>
                  </div>
                </div>
              ) : isOvertimeApprovalRecordName(data.recordName) && overtimeRequest ? (
                <div className="overflow-hidden rounded-md border border-border/80">
                  <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] text-sm">
                    <div className="bg-muted/40 px-4 py-3 font-medium text-foreground/80 border-b border-border/70">
                      {t('approvalDetailPage.overtimeEmployeeName', { defaultValue: 'Employee Name' })}
                    </div>
                    <div className="px-4 py-3 border-b border-border/70">
                      {requestedByName || '—'}
                    </div>

                    <div className="bg-muted/40 px-4 py-3 font-medium text-foreground/80 border-b border-border/70">
                      {t('approvalDetailPage.overtimeDate', { defaultValue: 'Over Time Date' })}
                    </div>
                    <div className="px-4 py-3 border-b border-border/70">
                      {formatDate(overtimeRequest.overTimeDate || null)}
                    </div>

                    <div className="bg-muted/40 px-4 py-3 font-medium text-foreground/80 border-b border-border/70">
                      {t('approvalDetailPage.overtimeTimeFrom', { defaultValue: 'Time From' })}
                    </div>
                    <div className="px-4 py-3 border-b border-border/70">
                      {formatTimeHm(overtimeRequest.timeFrom)}
                    </div>

                    <div className="bg-muted/40 px-4 py-3 font-medium text-foreground/80 border-b border-border/70">
                      {t('approvalDetailPage.overtimeTimeTo', { defaultValue: 'Time To' })}
                    </div>
                    <div className="px-4 py-3 border-b border-border/70">
                      {formatTimeHm(overtimeRequest.timeTo)}
                    </div>

                    <div className="bg-muted/40 px-4 py-3 font-medium text-foreground/80 border-b border-border/70">
                      {t('approvalDetailPage.overtimeTotalHours', { defaultValue: 'Total Hours' })}
                    </div>
                    <div className="px-4 py-3 border-b border-border/70">
                      {formatTimeHm(overtimeRequest.totalHours)}
                    </div>

                    <div className="bg-muted/40 px-4 py-3 font-medium text-foreground/80">
                      {t('approvalDetailPage.overtimeDetails', { defaultValue: 'Details' })}
                    </div>
                    <div className="px-4 py-3 break-words">
                      {overtimeRequest.details?.trim() || '—'}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">{t('approvalDetailPage.noEmployeeDetail')}</p>
              )}
            </CardContent>
          </Card>

          {/* Bottom left: Approvers */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">{t('approvalDetailPage.cardApprovers')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {approvers.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('approvalDetailPage.noApproversLoaded')}</p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border/80">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b-2 border-primary/15 bg-muted/40 hover:bg-muted/40">
                        <TableHead className="min-w-[min(14rem,40vw)] font-semibold text-primary">
                          {t('approvalDetailPage.approverColApprover', { defaultValue: 'Approver' })}
                        </TableHead>
                        <TableHead className="min-w-[8rem] font-semibold text-primary">
                          {t('approvalDetailPage.approverLblDepartment', { defaultValue: 'Department' })}
                        </TableHead>
                        <TableHead className="w-[110px] whitespace-nowrap font-semibold text-primary">
                          {t('approvalDetailPage.approverLblAction', { defaultValue: 'Action' })}
                        </TableHead>
                        <TableHead className="w-[150px] whitespace-nowrap font-semibold text-primary">
                          {t('approvalDetailPage.approverLblActionDate', { defaultValue: 'Action date' })}
                        </TableHead>
                        <TableHead className="min-w-[10rem] font-semibold text-primary">
                          {t('approvalDetailPage.approverLblNote', { defaultValue: 'Approval note' })}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {approvers.map((a) => {
                        const resolved =
                          a.empId && approverEmpNames[a.empId]?.trim()
                            ? approverEmpNames[a.empId].trim()
                            : ''
                        const display = resolved || a.userName || '—'
                        const initials = (resolved || a.userName || '')
                          .split(/\s+/)
                          .filter(Boolean)
                          .map((w) => w.replace(/^[^A-Za-z0-9]+/, '').charAt(0))
                          .filter(Boolean)
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()
                        return (
                          <TableRow key={a.id} className="border-b border-border/60">
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-3 py-1">
                                <Avatar className="h-10 w-10 shrink-0 border border-border/60">
                                  <AvatarFallback className="text-xs font-semibold">{initials || '—'}</AvatarFallback>
                                </Avatar>
                                <span className="min-w-0 font-medium leading-snug">{display}</span>
                              </div>
                            </TableCell>
                            <TableCell className="align-middle text-sm text-muted-foreground">
                              {a.departmentName?.trim() || '—'}
                            </TableCell>
                            <TableCell className="align-middle">
                              <Badge
                                variant={approverActionBadgeVariant(a.action || '')}
                                className="whitespace-nowrap text-xs font-medium"
                              >
                                {a.action?.trim() || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell className="align-middle text-sm text-muted-foreground whitespace-nowrap">
                              {formatApproverActionDate(a.actionDate)}
                            </TableCell>
                            <TableCell className="align-middle text-sm">
                              <div className="max-w-xs break-words rounded-md bg-muted/40 px-2 py-1.5 text-foreground/90">
                                {a.approvalNote?.trim() || '—'}
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column: Approval action form */}
        <Card>
          <CardHeader className="py-4">
            <CardTitle className="text-base">{t('approvalDetailPage.cardApproval')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <Label>{req} {t('approvalDetailPage.labelRequestedBy')}</Label>
              <Input readOnly value={requestedByName} className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>{req} {t('approvalDetailPage.labelRecordName')}</Label>
              <Input readOnly value={translateRecordName(data.recordName)} className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>{req} {t('approvalDetailPage.labelDepartment')}</Label>
              <Input readOnly value={departmentName} className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>{req} {t('approvalDetailPage.labelApprovalStatus')}</Label>
              <Select
                value={approvalStatusId ? String(approvalStatusId) : ''}
                onValueChange={(v) => {
                  const next = Number(v) || 0
                  // For Item Request: run quantity check immediately when selecting "Approved"
                  if (data && isItemRequestApprovalRecordName(data.recordName) && next) {
                    const code = approvalCodeFromId(next)
                    if (code === 435) {
                      void (async () => {
                        try {
                          const ok = await validateItemRequestAvailability()
                          if (ok) setApprovalStatusId(next)
                          // If not ok: keep previous selection (do not set to Approved)
                        } catch (e: any) {
                          await showError(
                            t('approvalDetailPage.qtyCheckFailedTitle', { defaultValue: 'Quantity check failed' }),
                            e?.response?.data?.message ?? e?.message ?? t('approvalDetailPage.toastSubmitFailed'),
                          )
                        }
                      })()
                      return
                    }
                  }
                  setApprovalStatusId(next)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('approvalDetailPage.placeholderSelectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.lookupNameEn || s.lookupNameAr || t('approvalMasterPage.unnamed')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('approvalDetailPage.labelApprovalNotes')}</Label>
              <Textarea
                value={approvalNote}
                onChange={(e) => setApprovalNote(e.target.value)}
                placeholder={t('approvalDetailPage.placeholderNotes')}
                rows={4}
              />
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="w-full">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('approvalDetailPage.submitting')}
                </>
              ) : (
                t('approvalDetailPage.submit')
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
